import type { PersistentChatWithMessages } from "~shared/types/persistent-chat-types";
import { DefaultChatModel, NEW_CHAT_ID } from "./PersistentChatTypes";

/**
 * When React still has {@link NEW_CHAT_ID} but `chat_acknowledged` already materialized a real id,
 * use the pending id so follow-up sends (e.g. cast-size picker Continue) stay on the CTA thread.
 */
/** `chat_acknowledged` replaced the compose placeholder with a persisted thread id. */
export function isExpectedNewChatMaterialization(
  previousChatId: string | null,
  nextChatId: string | null | undefined,
  pendingNewChatMaterializationId: string | null,
): boolean {
  return (
    previousChatId === NEW_CHAT_ID
    && nextChatId != null
    && nextChatId !== NEW_CHAT_ID
    && pendingNewChatMaterializationId != null
    && nextChatId === pendingNewChatMaterializationId
  );
}

export function resolveEffectiveChatId(
  chatId: string,
  pendingNewChatMaterializationId: string | null,
): string {
  if (chatId === NEW_CHAT_ID && pendingNewChatMaterializationId) {
    return pendingNewChatMaterializationId;
  }
  return chatId;
}

export const CTA_SEED_USER_MESSAGE_ID_PREFIX = "cta-seed-user-";

export function isCtaSeededUserMessage(
  message: { id?: string | null; metadata?: Record<string, unknown> | null } | null | undefined,
): boolean {
  if (!message) return false;
  if (message.metadata?.ctaSeed === true) return true;
  return typeof message.id === "string" && message.id.startsWith(CTA_SEED_USER_MESSAGE_ID_PREFIX);
}

/** Story Bible CTAs should continue the active persisted thread when one is already open (SW-10145). */
export function resolveStoryBibleCtaTargetChatId(
  chatId: string,
  pendingNewChatMaterializationId: string | null,
): string {
  const effectiveChatId = resolveEffectiveChatId(chatId, pendingNewChatMaterializationId);
  if (effectiveChatId !== NEW_CHAT_ID) {
    return effectiveChatId;
  }
  return NEW_CHAT_ID;
}

export function appendStoryBibleCtaSeededUserMessage(
  prev: PersistentChatWithMessages | undefined,
  args: {
    targetChatId: string;
    seededUserMessageId: string;
    userMessage: string;
  },
): PersistentChatWithMessages {
  const { targetChatId, seededUserMessageId, userMessage } = args;
  const base = prev ?? DefaultChatModel;
  const parentId = base.active_leaf_message_id ?? null;
  const seededMessage = {
    id: seededUserMessageId,
    role: "user" as const,
    content: userMessage,
    created_at: new Date(),
    metadata: { ctaSeed: true },
    ...(parentId != null ? { parent_message_id: parentId } : {}),
  };

  if (targetChatId === NEW_CHAT_ID) {
    return {
      ...DefaultChatModel,
      active_leaf_message_id: seededUserMessageId,
      messages: [seededMessage as PersistentChatWithMessages["messages"][number]],
    };
  }

  return {
    ...base,
    active_leaf_message_id: seededUserMessageId,
    messages: [...(base.messages || []), seededMessage as PersistentChatWithMessages["messages"][number]],
  };
}

/**
 * Decides whether `usePersistentChatStore().reset()` should run when the project is unchanged
 * but `chatId` changed. Reset aborts the agentic SSE stream — skip for benign id transitions.
 */
export function shouldResetPersistentChatOnSameProjectChatChange(
  previousChatId: string | null,
  nextChatId: string | null | undefined,
  ctaActionStatus: string | null | undefined,
  /** If set, skip reset when navigating from {@link NEW_CHAT_ID} to this exact id (from `chat_acknowledged`). */
  pendingNewChatMaterializationId: string | null = null,
): boolean {
  if (nextChatId == null) return false;

  // When leaving a persisted thread for the compose placeholder, never reset via this effect.
  // Explicit New Chat can set `awaiting_input` before `setChatId`, but the store can transiently read
  // as having no CTA on the same frame — a reset would wipe the seeded CTA and block InstantFire
  // until a second Story Bible click (SW-9180). New Chat triggers reset explicitly first.
  const openingComposeFromPersistedThread =
    nextChatId === NEW_CHAT_ID
    && previousChatId !== null
    && previousChatId !== NEW_CHAT_ID;

  if (openingComposeFromPersistedThread) return false;

  const ctaAwaitingInputOnNewChat =
    nextChatId === NEW_CHAT_ID && ctaActionStatus === "awaiting_input";

  const isExpectedChatMaterialization =
    previousChatId === NEW_CHAT_ID
    && nextChatId !== NEW_CHAT_ID
    && pendingNewChatMaterializationId != null
    && nextChatId === pendingNewChatMaterializationId;

  return !ctaAwaitingInputOnNewChat && !isExpectedChatMaterialization;
}

/**
 * Snapshot an unanswered `ask_user_question` for the chat being left, before reset clears store state.
 */
export function stashSnapshotForUnansweredQuestion<TQuestion>(
  previousChatId: string | null,
  questionOwnerChatId: string | null,
  currentUserQuestion: TQuestion | null,
): { previousChatId: string; question: TQuestion } | null {
  if (!previousChatId || currentUserQuestion == null) return null;
  if (questionOwnerChatId !== previousChatId) return null;
  return { previousChatId, question: currentUserQuestion };
}
